<template>
    <v-card
        id="editor-settings"
        elevation="1"
        :class="cardClass"
        :loading="loading"
        @click.passive="setActive()"
    >
        <expansion-panel-header
            :tooltipText="tooltipText"
            :collapsable="collapsable"
            :collapsed="settingsCollapsed"
            :loading="loading"
            onlyIconCanCollapse
            @click="settingsCollapsed = !settingsCollapsed"
        >
            <v-autocomplete
                :label="t_settings_of"
                :aria-label="t_settings_of"
                v-model="active_dropdown"
                :items="dropdown_elements"
                outlined dense hide-details="auto"
                @change="dropdownUpdated()"
                @focus="onDropdownFocus">
            </v-autocomplete>
        </expansion-panel-header>

        <div v-show="!settingsCollapsed || grid==='3cols'">
            <div class="placeholder"></div>
            <div ref="scrollBox" class="scrollBox" :style="heightStyle" @scroll="scrolled()">

                <settings-container v-if="sectionAvailable('widget')"
                                    :title="t_widget_settings"
                                    initially-open>
                    <widget-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :element_nr="inner_properties.index"
                        :loading="loading"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        :widget_settings="widget_settings[inner_properties.id]"
                        :widget_properties="widget_properties[inner_properties.id]"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </widget-settings>
                </settings-container>

                <div class="noCollapse">
                    <div v-if="readonly" class="alert alert-danger">
                        {{ t_settings_readonly }}
                    </div>

                    <div v-if="creator === false" class="alert alert-danger">
                        <div>{{ t_creator_invalid }}</div>
                        <template v-for="element in creator_errors">
                            <template v-if="element.errors.length > 0">
                                <div>{{ element.name }}</div>
                                <ul>
                                    <li v-for="error in element.errors">
                                        {{ error }}
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </div>

                    <common-no-collapse-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        :all_layer_layers="all_layer_layers"
                        :parent_layers="template_layers"
                        :esign-connection-direct="esignConnectionDirect"
                        :grid="grid"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">

                        <div class="flex-dropzone"
                             v-if="propertyAvailable('dropzone') && propertyAvailable('dropzone')['style'] === 'basic'">
                            <v-row>
                                <v-col cols="12" v-if="propertyAvailable('name')">
                                    <v-text-field
                                        v-model="inner_properties.name"
                                        :label="t_name"
                                        :disabled="element_readonly('name')"
                                        @input="nameUpdated()"
                                        :rules="[rules.required]"
                                        :outlined="outlined" :dense="dense" hide-details="auto">
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" >
                                    <div :class="{'v-input--is-disabled': element_readonly('dropzone')}"
                                         class="v-input v-input--dense theme--light v-text-field v-text-field--is-booted v-text-field--enclosed v-text-field--outlined">
                                        <div class="v-input__control">
                                            <div class="v-input__slot" style="cursor:default;">
                                                <fieldset aria-hidden="true">
                                                    <legend style="width: 0px;"><span class="notranslate">​</span>
                                                    </legend>
                                                </fieldset>
                                                <div class="v-text-field__slot">
                                                    <label for="input-background"
                                                       class="v-label v-label--active theme--light"
                                                       :class="{'v-label--is-disabled': element_readonly('dropzone')}"
                                                       style="left: 0px; right: auto; position: absolute; background:white; margin-left:-5px; padding:0 5px;">
                                                        {{ dropzone_title }}
                                                    </label>
                                                    <div id="input-background"
                                                         :data-type="dropzoneType"
                                                         :class="{'dropzone-readonly': element_readonly('dropzone')}"
                                                         class="replace-dropzone"
                                                          :style="dropzoneStyles">
                                                    </div>
                                                </div>
                                            </div>
                                            <div v-if="!element_readonly('dropzone')"
                                                 class="v-text-field__details">
                                                <div class="v-messages theme--light">
                                                    <div class="v-messages__wrapper">
                                                        <div class="v-messages__message"> {{ dropzone_description }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </div>
                        <v-row>
                            <v-col cols="6" v-if="propertyAvailable('valid_from')">
                                <datepicker v-model="inner_properties.parameters['valid-from']"
                                            :disabled="element_readonly('valid_from')"
                                            :label="t_valid_from"
                                            :max="inner_properties.parameters['valid-to']"
                                            @blur="leaveElementToBeScrolled()"
                                            @focus="initToBeScrolled('datepicker', 'datepicker-input-valid_from')" />
                            </v-col>
                            <v-col cols="6" v-if="propertyAvailable('valid_to')">
                                <datepicker v-model="inner_properties.parameters['valid-to']"
                                            :disabled="element_readonly('valid_to')"
                                            :label="t_valid_to"
                                            :min="inner_properties.parameters['valid-from']"
                                            align-right
                                            @blur="leaveElementToBeScrolled()"
                                            @focus="initToBeScrolled('datepicker', 'datepicker-input-valid_to')" />
                            </v-col>
                        </v-row>


                        <v-row v-if="propertyAvailable('edit_buttons') && !element_readonly('edit_buttons')">
                            <v-col cols="12">
                                <v-btn small :class="saving ? 'disabled' : ''" class="m-b-xs"
                                       @click="updateTemplateAndRedirect(inner_properties.id, inner_properties.type)">
                                    <i v-if="saving === true" class="fa-regular fa-spinner fa-spin-pulse fa-fw m-r-xs"></i>
                                    <i v-else class="fa-regular fa-pencil fa-fw m-r-xs"></i>
                                    {{ inner_properties.type === 'template' ? t_edit_template : t_edit_campaign }}
                                </v-btn>
                                <v-btn v-if="inner_properties.state === 'link'" class="m-b-xs"
                                       small :class="saving ? 'disabled' : ''"
                                       @click="convertLink(inner_properties.id, inner_properties.type)">
                                    <i class="fa-regular fa-link-simple-slash m-r-xs" aria-hidden="true"></i>
                                    {{ t_local_copy }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </common-no-collapse-settings>
                </div>

                <settings-container v-if="sectionAvailable('font')"
                                    :title="t_font">
                    <font-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </font-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('extended')"
                                    :title="t_extended_properties">
                    <extended-settings
                        v-model="inner_properties"
                        :definition="elementProperties()"
                        :readonly="readonly"
                        :advanced-privilege="advancedPrivilege"
                        :basic-privilege="basicPrivilege"
                        v-on:initToBeScrolled="initToBeScrolled($event['type'], $event['id'])"
                        v-on:leaveElementToBeScrolled="leaveElementToBeScrolled()">
                    </extended-settings>
                </settings-container>

                <settings-container v-if="sectionAvailable('user')"
                                    :title="t_user_properties">
                        <v-row v-if="propertyAvailable('description')">
                            <v-col cols="12">
                                <v-text-field
                                    v-model="inner_properties.description"
                                    :label="t_description"
                                    :disabled="element_readonly('description')"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-text-field>
                            </v-col>
                        </v-row>

                        <v-row v-if="propertyAvailable('locked') && !element_readonly('locked')">
                            <v-col cols="12">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="inner_properties.locked"
                                    :checked="inner_properties.locked"
                                    :value="inner_properties.locked"
                                    :label="t_locked"
                                    :disabled="element_readonly('locked')"
                                    @click="inner_properties.locked = !inner_properties.locked"
                                />
                                {{ t_locked }}
                            </v-col>
                        </v-row>

                        <v-row v-if="propertyAvailable('usergroups')" style="margin-top: 0;">
                            <v-col cols="12" id="select-usergroups">
                                <v-autocomplete
                                    v-model="inner_properties.usergroups"
                                    :label="t_usergroups"
                                    :items="all_usergroups"
                                    attach="#select-usergroups .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto"
                                    multiple
                                    :disabled="element_locked('usergroups')">
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="12">
                                <div id="checkbox-strict_mode">
                                    <Checkbox
                                        class="mr-2"
                                        :binary="true"
                                        :modelValue="inner_properties.strict_mode"
                                        :label="t_strict_mode"
                                        :disabled="element_locked('strict_mode')"
                                        @click="inner_properties.strict_mode = !inner_properties.strict_mode"
                                    />
                                    {{ t_strict_mode }}
                                </div>
                                <v-tooltip-wrapper activator="#checkbox-strict_mode">
                                    <span>{{ t_strict_mode_description }}</span>
                                </v-tooltip-wrapper>
                            </v-col>
                        </v-row>

                        <div class="row">
                            <div class="form-group col-md-6" v-if="propertyAvailable('elementLeft')">
                                <number-field
                                    v-model="real_value.position.left"
                                    :label="t_left"
                                    :disabled="element_locked('elementLeft')"
                                    :limits="getFieldEvaluatedLimits('elementLeft')"
                                    @change="checkBoundaries()"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                            <div class="form-group col-md-6" v-if="propertyAvailable('elementTop')">
                                <number-field
                                    v-model="real_value.position.top"
                                    :label="t_top"
                                    :disabled="element_locked('elementTop')"
                                    :limits="getFieldEvaluatedLimits('elementTop')"
                                    @change="checkBoundaries()"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                        </div>
                        <div class="row">
                            <div class="form-group col-md-6" v-if="propertyAvailable('elementWidth')">
                                <number-field
                                    v-model="real_value.size.width"
                                    :label="t_size_width"
                                    :disabled="element_locked('elementWidth')"
                                    :limits="getFieldEvaluatedLimits('elementWidth')"
                                    @change="checkBoundaries('widthOrHeight')"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                            <div class="form-group col-md-6" v-if="propertyAvailable('elementHeight')">
                                <number-field
                                    v-model="real_value.size.height"
                                    :label="t_size_height"
                                    :disabled="element_locked('elementHeight')"
                                    :limits="getFieldEvaluatedLimits('elementHeight')"
                                    @change="checkBoundaries('widthOrHeight')"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                        </div>

                        <v-row v-if="propertyAvailable('layers')">
                            <v-col cols="12">
                                <Checkbox
                                    class="mr-2"
                                    :binary="true"
                                    :modelValue="ignore_layer_size"
                                    :label="t_ignore_layer_resolution"
                                    :disabled="element_locked('layers')"
                                    @click="ignore_layer_size = !ignore_layer_size"
                                />
                                {{ t_ignore_layer_resolution }}
                            </v-col>
                            <v-col cols="12" id="select-layers">
                                <v-autocomplete
                                    v-model="inner_properties.layers"
                                    :label="t_layers"
                                    :items="select_layer_items()"
                                    attach="#select-layers .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto"
                                    multiple
                                    :disabled="element_locked('usergroups')">
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row v-if="propertyAvailable('tags')">
                            <v-col cols="12" id="select-tags">
                                <autocomplete-addable
                                    id="autocomplete-tags"
                                    v-model="inner_properties.tags"
                                    :label="t_tags"
                                    :items="all_tags"
                                    attach="#select-tags .v-select"
                                    :outlined="outlined" :dense="dense" hide-details="auto"
                                    multiple="multiple"
                                    :disabled="element_locked('tags')"
                                    :createable-values="tagCreationPrivilege">
                                </autocomplete-addable>
                            </v-col>
                        </v-row>
                        <div class="row">
                            <div :class="[widthChanged ? 'has-warning' : '' , 'form-group col-md-4']" v-if="propertyAvailable('width')">
                                <number-field
                                    v-model="width"
                                    :label="t_width"
                                    :disabled="element_locked('width') || inline === 'true'"
                                    :limits="getFieldEvaluatedLimits('width')"
                                    :warning="widthChanged"
                                    :hint="widthChanged ? t_size_changed_description : ''"
                                    :persistentHint="widthChanged"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                            <div :class="[heightChanged ? 'has-warning' : '' , 'form-group col-md-4']" v-if="propertyAvailable('height')">
                                <number-field
                                    v-model="height"
                                    :label="t_height"
                                    :disabled="element_locked('height') || inline === 'true'"
                                    :limits="getFieldEvaluatedLimits('height')"
                                    :warning="heightChanged"
                                    :hint="heightChanged ? t_size_changed_description : ''"
                                    :persistentHint="heightChanged"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </number-field>
                            </div>
                            <div class="form-group col-md-4" v-if="propertyAvailable('ratio')">
                                <v-text-field
                                    :value="ratio"
                                    :label="t_format"
                                    :readonly="true"
                                    :outlined="outlined" :dense="dense" hide-details="auto">
                                </v-text-field>
                            </div>
                        </div>
                    </settings-container>
            </div>
        </div>
    </v-card>
</template>

<script>
    import debounce from 'debounce';
    import datepicker from '../../common/datepicker.vue';
    import Autocomplete from 'vue2-autocomplete-js';
    import utility from '../../../utility';

    import commonSettingsMixin from "../settings/commonSettingsMixin";
    import commonNoCollapseSettings from "../settings/commonNoCollapseSettings";
    import fontSettings from "../settings/fontSettings";
    import extendedSettings from "../settings/extendedSettings";
    import moment from 'moment'
    import widgetSettings from "../settings/widgetSettings";
    import elementTypes from "../elements/types";
    import toaster from "../../common/toaster";
    import VTooltipWrapper from '../../common/v-tooltip-wrapper.vue'
    import PublishTooltipButton from "../common/publish-tooltip-button.vue";
    import SettingsContainer from "../publish/settings-container.vue";
    import ExpansionPanelHeader from "../common/expansion-panel-header.vue";
    import {TIMEOUT_DURATION} from "../common/HeightCalculator";
    import HeaderCheckbox from "../common/PrimeVue/HeaderCheckbox.vue";
    import Checkbox from "../common/PrimeVue/Checkbox.vue";

    export default {
        components: {
            ExpansionPanelHeader,
            SettingsContainer,
            PublishTooltipButton,
            VTooltipWrapper,
            datepicker,
            Autocomplete,
            commonNoCollapseSettings,
            fontSettings,
            extendedSettings,
            widgetSettings,
            HeaderCheckbox,
            Checkbox
        },
        mixins: [
            commonSettingsMixin,
        ],
        props: [
            'value',
            'data',
            'inline',
            'basicPrivilege',
            'advancedPrivilege',
            'esignConnectionDirect',
            'creator',
            'grid',
            'startCollapsed',
            'startActive',
            'back_url',
            'parent_layer_limitation',
            'parent_usergroup_limitation',
            'editPrivilegeMediapool',
            'editPrivilegeTemplate',
            'editPrivilegeCampaign',
            'tagCreationPrivilege'
        ],
        data: function () {
            return {
                t_valid_from: Lang.langGet("Not before"),
                t_valid_to: Lang.langGet("Not after"),
                t_size_changed_description: Lang.langGet("Changes in size are visible only after saving!"),
                t_ignore_layer_resolution: Lang.langGet("Ignore resolution"),
                t_creator_invalid: Lang.langGet("Template cannot be saved:"),
                t_required: Lang.langGet("Required"),
                ignore_layer_size: false,
                inner_properties: {},
                empty_inner_properties: {
                    background_image: '/img/shim.png',
                    type: 'background',
                    position: {
                        left: 0,
                        top: 0,
                        zindex: 1
                    },
                    size: {
                        width: 1,
                        height: 1,
                        ratio: 0
                    },
                    usergroups: [],
                    layers: [],
                    locked: false,
                    readonly: false,
                    tags: [],
                    parameters: {
                        resolution: '1x1',
                        format: '1:1',
                        'valid-from': '',
                        'valid-to': ''
                    },
                },
                prev_inner_properties: null,
                activeChanged: false,
                real_value: {
                    position: {
                        left: 0,
                        top: 0
                    },
                    size: {
                        width: 1,
                        height: 1
                    }
                },
                all_usergroups: [],
                all_layers: [],
                all_active_layers: [],
                all_layer_layers: [],
                all_active_layer_layers: [],
                limited_layer_layers: [],
                limited_active_layer_layers: [],
                all_tags: [],
                width: 1,
                height: 1,
                saving: false,
                redirect_url: null,
                id: 0,
                revision_origin: 0,
                active_dropdown: 0,
                dropdown_elements: [
                    {value: 0, text: Lang.langChoice("Template|Templates", 1), element: {}}
                ],
                frame: {},

                initValidation: false,
                initElements: [],
                opened_panels: [],

                template_layers: [],
                prevID: null,
                date_format_mapping: {
                    'dd.MM.yyyy': 'DD.MM.yyyy',
                    'MM/dd/yyyy': 'MM/DD/yyyy'
                },

                outlined: true,
                dense: true,
                heightStyle: undefined,

                rules: {
                    required: value => !!value || this.t_required,
                }
            }
        },
        computed: {
            cardClass() {
                return 'float-e-margins no-margin template-editor fixed-height';
            },
            collapsable() {
                return this.grid === '2cols';
            },
            heightChanged: function () {
                return this.height !== this.real_value.size.height;
            },
            widthChanged: function () {
                return this.width !== this.real_value.size.width;
            },
            dropzone_title: function () {
                let type = this.inner_properties.type;

                if (!type || type === 'background') {
                    name =  'Background';
                } else {
                    name =  elementTypes.titles[type];
                }
                return Lang.langGet(name);
            },
            dropzone_description: function () {
                let type = this.inner_properties.type;

                if (!type || type === 'background') {
                    name =  'Picture';
                } else {
                    name = elementTypes.titles[type];
                }

                return Lang.langGet("Drag & Drop \":type\" media above to replace the current one", {type: Lang.langGet(name)});
            },
            dropzoneStyles: function () {
                return {
                    backgroundImage: "url('" + (this.inner_properties.background_image??"") + "')",
                    backgroundPosition: 'center center',
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    marginTop: '12px',
                    marginBottom: '12px'
                }
            },
            dropzoneType: function () {
                switch (this.inner_properties.type) {
                    case 'background':
                        return 'picture';
                    default:
                        return this.inner_properties.type;
                }
            },
            innerLayers: function() {
                return this.inner_properties.layers;
            },
            innerTags: function() {
                return this.inner_properties.tags;
            },
            innerUsergroups: function() {
                return this.inner_properties.usergroups;
            },
            inner_primary_color: {
                get: function () {
                    return this.innerFieldGetter('primary_color');
                },
                set: function (val) {
                    this.innerPropertySetter('primary_color', val);
                }
            },
            inner_secondary_color: {
                get: function () {
                    return this.innerFieldGetter('secondary_color');
                },
                set: function (val) {
                    this.innerPropertySetter('secondary_color', val);
                }
            },
            tooltipText() {
                const selectedElementTitle = this.dropdown_elements.find(element => element.value == this.active_dropdown).text
                return `${this.settingsCollapsed ? this.t_expand : this.t_collapse} ${this.t_settings_of} ${selectedElementTitle}`
            }
        },
        watch: {
            grid(newVal) {
                if (newVal === '2cols') this.settingsCollapsed = false;

                this.timeoutHeightRecalculation();
            },
            settingsCollapsed() {
                this.timeoutHeightRecalculation();
                EaseEventBus.fire('WORKAROUND_recalculate_dragdrop_height');
            },
            value: {
                handler: function () {
                    // prevent endless-loop
                    if (JSON.stringify(this.inner_properties) !== JSON.stringify(this.value)) {
                        this.activeChanged = true;
                        this.old_text_direction = null;
                        this.inner_properties = _.extend({}, this.empty_inner_properties, this.value);
                        this.loadDefinition();

                        if(this.inner_properties.type === 'background') {
                            this.template_layers = this.inner_properties.layers;
                        }

                        let resolution = this.inner_properties.parameters.resolution.split('x');
                        this.width = resolution[0];
                        this.height = resolution[1];

                        this.$nextTick(() => {
                            if (this.colorpicker_primary !== null) {
                                this.colorpicker_primary.colorpicker('destroy');
                                this.colorpicker_primary = null;
                            }

                            if (this.colorpicker_secondary !== null) {
                                this.colorpicker_secondary.colorpicker('destroy');
                                this.colorpicker_secondary = null;
                            }

                            this.initializeColorpickers();
                        });
                        this.activeChanged = false;
                    }

                    EaseEventBus.fire('elementEditButtonShown', !!(this.propertyAvailable('edit_buttons') && !this.element_readonly('edit_buttons')));
                },
                deep: true
            },
            inner_properties: {
                handler: debounce(function (val) {
                    this.autoUpdateTextAlignment();

                    if(!this.unsavedChanges && this.activeChanged === false && this.innerPropertiesChanged()) {
                        EaseEventBus.fire("unsavedChanges", true);
                    }

                    let positionLeft = Math.max(0, Math.round(parseFloat(val.position.left) / parseFloat(this.frame.ratio)));
                    let positionTop = Math.max(0, Math.round(parseFloat(val.position.top) / parseFloat(this.frame.ratio)));
                    this.real_value.position.left = positionLeft;
                    this.real_value.position.top = positionTop;
                    this.real_value.size.width = Math.round(parseFloat(val.size.width) / parseFloat(this.frame.ratio));
                    this.real_value.size.height = Math.round(parseFloat(val.size.height) / parseFloat(this.frame.ratio));

                    if (typeof val.type !== 'undefined' && val.type === 'layer' && typeof val.layer !== 'undefined' && val.layer.length > 0) {
                        this.current_layer_layer = this.getLayerNr([String(val.layer)])[0];
                    }

                    this.loadDefinition();

                    this.validate();
                    this.$emit('input', val);
                }, 500),
                deep: true
            },
            innerLayers: {
                handler: function () {
                    EaseEventBus.fire("layersSelectionChanged", this.innerLayers ? this.innerLayers : []);
                },
                deep: true
            },
            innerTags: {
                handler: function () {
                    EaseEventBus.fire("tagsSelectionChanged", this.innerTags ? this.innerTags : []);
                },
                deep: true
            },
            innerUsergroups: {
                handler: function () {
                    EaseEventBus.fire("usergroupsSelectionChanged", this.innerUsergroups ? this.innerUsergroups : []);
                },
                deep: true
            },
            real_value: {
                handler: function (val, oldVal) {
                    if (val.position.left.length > 10 || isNaN(val.position.left)) {
                        val.position.left = 1;
                    }

                    if (val.position.top.length > 10 || isNaN(val.position.top)) {
                        val.position.top = 1;
                    }

                    if (val.size.width.length > 10 || isNaN(val.size.width)) {
                        val.size.width = 1;
                    }

                    if (val.size.height.length > 10 || isNaN(val.size.height)) {
                        val.size.height = 1;
                    }

                    let positionLeft = Math.max(0, parseInt(val.position.left) * this.frame.ratio);
                    let positionTop = Math.max(0, parseInt(val.position.top) * this.frame.ratio);
                    this.inner_properties.position.left = positionLeft;
                    this.inner_properties.position.top = positionTop;
                    this.inner_properties.size.width = parseInt(val.size.width) * this.frame.ratio;
                    this.inner_properties.size.height = parseInt(val.size.height) * this.frame.ratio;
                },
                deep: true
            },
            width: function (val, oldVal) {
                var clamped = this.clampToLimits(val, oldVal, this.getFieldLimits("width"));
                Vue.set(this, "width", 0);
                Vue.set(this, "width", clamped);
                this.inner_properties.parameters.resolution = this.width + 'x' + this.height;
            },
            height: function (val, oldVal) {
                var clamped = this.clampToLimits(val, oldVal, this.getFieldLimits("height"));
                Vue.set(this, "height", 0);
                Vue.set(this, "height", clamped);
                this.inner_properties.parameters.resolution = this.width + 'x' + this.height;
            },
            ratio: function (val, oldVal) {
                this.inner_properties.parameters.format = val;
            },
        },
        methods: {
            timeoutHeightRecalculation(forceRecalculation) {
                setTimeout(() => {
                    this.heightStyle = this.settingsStyle(forceRecalculation);
                }, TIMEOUT_DURATION);
            },
            autoUpdateTextAlignment: function () {
                if(this.old_text_direction !== null) {
                    if (this.old_text_direction !== this.inner_properties.text_direction) {
                        if (this.inner_properties.text_direction === 'rtl' && this.inner_properties.text_align === 'left') {
                            this.inner_properties.text_align = 'right';
                        }

                        if (this.inner_properties.text_direction === 'ltr' && this.inner_properties.text_align === 'right') {
                            this.inner_properties.text_align = 'left';
                        }
                    }
                }
                this.old_text_direction = this.inner_properties.text_direction;
            },
            innerPropertiesChanged: function () {
                let changes = false;
                if(this.prev_inner_properties !== null && this.prev_inner_properties.index === this.inner_properties.index) {
                    let type = this.inner_properties.type;
                    for (const [property, def] of Object.entries(this.elementProperties())) {
                        let value = "";
                        let prev_value = "";
                        if (def.computed) {
                            value = _.get(this.inner_properties, def.computed);
                            prev_value = _.get(this.prev_inner_properties, def.computed);
                        } else {
                            value = this.inner_properties[property];
                            prev_value = this.prev_inner_properties[property];
                        }
                        //for numerical stability
                        if(!isNaN(value) && value !== '') {
                            value = Math.round(value * 100000) / 100000;
                        }
                        if(!isNaN(prev_value) && prev_value !== '') {
                            prev_value = Math.round(prev_value * 100000) / 100000;
                        }

                        let value_string = '';
                        let prev_value_string = '';
                        if (typeof value === "object") {
                            value_string = JSON.stringify(value);
                        } else {
                            value_string = String(value);
                        }
                        if (typeof prev_value === "object") {
                            prev_value_string = JSON.stringify(prev_value);
                        } else {
                            prev_value_string = String(prev_value);
                        }

                        if(value_string !== prev_value_string) {
                            if(prev_value != null || value !== this.defaultValue(type, property)) {
                                changes = true;
                                break;
                            }
                        }
                    }
                }
                this.prev_inner_properties = JSON.parse(JSON.stringify(this.inner_properties));
                return changes;
            },
            select_layer_items: function () {
                let items = this.limited_layer_layers.filter((layer) => {
                    return this.is_selectable_layer(layer.value, 'template') === true;
                });
                items = items.map((layer) => {
                    layer.disabled = this.is_disabled_layer(layer, this.width, this.height, this.ignore_layer_size);
                    layer.value = layer.value.toString();
                    return layer;
                });
                return items;
            },
            loadDefinition: function () {
                let type = this.inner_properties.type;
                this.loadDefinitionForType(type, "t_");
            },
            validate: function (element = null, id = null, init = false) {
                if (element === null || id === null) {
                    element = this.inner_properties;
                    id = this.active_dropdown;
                }

                let validationResult = {valid: true, errors: []}, toBeValidated = {};

                if (this.inner_properties.type === 'background') {
                    toBeValidated["valid-from"] = this.inner_properties.parameters['valid-from'];
                    toBeValidated["valid-to"] = this.inner_properties.parameters['valid-to'];
                    validationResult = utility.validate(toBeValidated);
                }

                validationResult = this.validateNumberProperties(element, validationResult, "t_");
                this.creator_errors.splice(id, 1, {
                    'name': this.t_title(element, id),
                    'errors': validationResult.errors
                });
                let valid = validationResult.valid;
                if (valid) {
                    for (id in this.creator_errors) {
                        element = this.creator_errors[id];
                        if (element.errors.length > 0) {
                            valid = false;
                            break;
                        }
                    }
                }

                this.$emit('validation', valid);
            },
            validateInit: function () {
                this.init = true;
                if (this.initValidation === false) {
                    if (this.dropdown_elements.length > 0 && Object.keys(this.frame).length > 0) {
                        let id, element;
                        for (id in Object.keys(this.initElements)) {
                            element = this.initElements[id];
                            element.description = this.dropdown_elements[id].element.description;
                            element.name = this.dropdown_elements[id].element.name;

                            this.validate(element, id, true);
                        }
                        this.initValidation = true;
                        this.initElements = [];
                    }
                }
                this.init = false;
            },
            recalculateHeight: function () {
                if (this.inner_properties.stretch === 'original' && this.inner_properties.size.ratio > 0) {
                    this.real_value.size.height = this.real_value.size.width * this.inner_properties.size.ratio;
                }

                this.checkBoundaries('widthOrHeight');
            },
            recalculateWidth: function () {
                if (this.inner_properties.stretch === 'original' && this.inner_properties.size.ratio > 0) {
                    this.real_value.size.width = this.real_value.size.height / this.inner_properties.size.ratio;
                }

                this.checkBoundaries('widthOrHeight');
            },
            checkBoundaries: function (field = '') {
                let width = this.real_value.size.width === '' ? 1 : parseInt(this.real_value.size.width),
                    height = this.real_value.size.height === '' ? 1 : parseInt(this.real_value.size.height),
                    left = this.real_value.size.left === '' ? 0 : parseInt(this.real_value.position.left),
                    top = this.real_value.size.top === '' ? 0 : parseInt(this.real_value.position.top),
                    templateWidth = parseInt(this.frame.width / this.frame.ratio),
                    templateHeight = parseInt(this.frame.height / this.frame.ratio);

                this.real_value.size.width = width;
                this.real_value.size.height = height;
                this.real_value.size.left = left;
                this.real_value.size.top = top;

                if (left > templateWidth) {
                    let tmpLeft = Math.max(0, templateWidth - this.real_value.size.width);
                    this.real_value.position.left = tmpLeft;
                    left = parseInt(tmpLeft);
                }

                if (top > templateHeight) {
                    let tmpTop = Math.max(0, templateHeight - this.real_value.size.height);
                    this.real_value.position.top = tmpTop;
                    top = parseInt(tmpTop);
                }

                if (left + width > templateWidth) {
                    if (field === 'widthOrHeight') {
                        this.real_value.position.left = Math.max(0, templateWidth - width);
                        this.checkBoundaries();
                    } else {
                        this.real_value.size.width = Math.max(1, templateWidth - left);
                        this.recalculateHeight();
                    }
                }

                if (top + height > templateHeight) {
                    if (field === 'widthOrHeight') {
                        this.real_value.position.top = Math.max(0, templateHeight - height);
                        this.checkBoundaries();
                    } else {
                        this.real_value.size.height = Math.max(1, templateHeight - top);
                        this.recalculateWidth();
                    }
                }
            },
            updateTemplateAndRedirect: function (id, type) {
                this.saving = true;

                if (type === 'template') {
                    this.redirect_url = '/content/template/edit-inline/' + id + '/latest';
                } else {
                    this.redirect_url = '/content/campaign/edit-inline/' + id + '/latest';
                }

                EaseEventBus.fire('updateTemplate', {refresh: false, back: false});
            },
            initLayerLimits: function (template_layers, elements) {
                this.current_layer_template = _.max(this.getLayerNr(template_layers));
                let minLayer = Laravel.maxLayers; // Current max is 64

                _.forEach(elements, (value, key) => {
                    if (typeof value.type !== 'undefined' && value.type === 'layer' && (typeof value.layer !== 'undefined' || value.layer !== null)) {
                        let tmp = this.getLayerNr([String(value.layer)])[0];
                        if (tmp < minLayer) {
                            minLayer = tmp;
                        }
                    }
                });

                if (minLayer === Laravel.maxLayers) {
                    // If no layer is below the max, it is not restricted
                    this.current_layer_layer = -1;
                } else {
                    this.current_layer_layer = minLayer;
                }
            },
            convertLink: function (id, type) {
                axios.post('/content/template/convert', {
                    id: id,
                    project: Laravel.activeProject,
                    type: type
                }).then(() => {
                    this.inner_properties.state = 'copy';
                }).catch((error) => {
                    if (error.response.status === 401) {
                        window.location = '/login';
                    } else if (error.response.status === 403) {
                        toaster.error(this.t_not_enough_privileges);
                    } else if (error.response.status === 422) {
                        let errorString = '';
                        for (let key in error.response.data) {
                            errorString = errorString + error.response.data[key][0] + ' ';
                        }
                        toaster.error(errorString);
                    } else {
                        toaster.error(this.t_link_convert_failed);
                    }
                });
            },
            dropdownUpdated: function () {
                EaseEventBus.fire('selectViaDropdown', this.active_dropdown);
            },
            nameUpdated: function () {
                EaseEventBus.fire('templateNameUpdated', this.inner_properties.name);
            },
            t_title: function (element, index) {
                let description = '';
                let type = '';
                let name = '';

                if (this.active_dropdown === index) {
                    if (this.inner_properties.description) {
                        description = this.inner_properties.description;
                    }
                    if (this.inner_properties.type) {
                        type = this.inner_properties.type;
                    }
                    if (this.inner_properties.name) {
                        name = this.inner_properties.name;
                    }
                } else {
                    description = element.description || '';
                    type = element.type || '';
                    name = element.name || '';
                }

                if (name !== '' && description !== '') {
                    name = '[' + name + '] ';
                }
                if (name !== '' || description !== '') {
                    name = ' - ' + name;
                }
                if (type !== '') {
                    if (type === 'background') {
                        type = this.t_template;
                        name = '';
                        description = '';
                    } else {
                        if (type === 'campaign' && this.inner_properties.dynamic) {
                            type = Lang.langGet("Dynamic Campaign");
                        } else {
                            type = Lang.langGet(elementTypes.titles[type]);
                        }
                    }
                }
                return type + name + description;
            },
            formatDate(value) {
                if(value) {
                    let savedFormat = 'MM/DD/yyyy';
                    if (value.includes('.')) {
                        savedFormat = 'DD.MM.yyyy';
                    }

                    let date = moment(value, savedFormat);
                    return date.isValid() ? date.format(this.date_format_mapping[Laravel.activeDateformat]) : '';
                } else {
                    return "";
                }
            },
            onDropdownFocus() {
                if(this.settingsCollapsed) this.settingsCollapsed = false
            }
        },
        created: function () {
            this.inner_properties = _.extend({}, this.empty_inner_properties);

            let json = JSON.parse(this.data);
            if (Object.keys(json).length > 0) {
                this.all_usergroups = json.all_usergroups.map((item) => { item.value = item.value.toString(); return item });
                this.all_layers = json.all_layers.map((item) => { item.value = item.value.toString(); return item });
                this.all_active_layers = json.all_active_layers.map((item) => { item.value = item.value.toString(); return item });
                this.all_tags = _.orderBy(json.all_tags, 'text').map((item) => { item.value = item.value.toString(); return item });
            }

            EaseEventBus.listen('onElementEditButtonClick', () => this.updateTemplateAndRedirect(this.inner_properties.id, this.inner_properties.type))
        },
        mounted() {
            let json = JSON.parse(this.data);

            this.unsavedChanges = true;
            this.loadDefinition();
            this.$nextTick(() => {
                this.unsavedChanges = false;
            })

            if (Object.keys(json).length > 0) {
                this.id = json.id;
                this.revision_origin = json.revision_origin;
                this.initLayerLimits(json.layers, json.elements);

                if (Laravel.switch_off_times === true) {
                    this.all_layer_layers = _.remove(JSON.parse(JSON.stringify(this.all_layers)), function (o) {
                        return o.nr !== 0;
                    });
                    this.all_active_layer_layers = _.remove(JSON.parse(JSON.stringify(this.all_active_layers)), function (o) {
                        return o.nr !== 0;
                    });
                } else {
                    this.all_layer_layers = this.all_layers;
                    this.all_active_layer_layers = this.all_active_layers;
                }

                var parentLayerLimitation = [];
                var parentUsergroupLimitation = [];
                if(this.parent_layer_limitation) {
                    parentLayerLimitation = JSON.parse(this.parent_layer_limitation);
                }
                if(this.parent_usergroup_limitation) {
                    parentUsergroupLimitation = JSON.parse(this.parent_usergroup_limitation);
                }
                if(parentLayerLimitation.length > 0) {
                    this.limited_active_layer_layers = this.all_active_layer_layers.filter(o => parentLayerLimitation.includes(o.value.toString()));
                    this.limited_layer_layers = this.all_layer_layers.filter(o => parentLayerLimitation.includes(o.value.toString()));
                } else {
                    this.limited_active_layer_layers = this.all_active_layer_layers;
                    this.limited_layer_layers = this.all_layer_layers;
                }
                if(parentUsergroupLimitation.length > 0) {
                    this.all_usergroups = this.all_usergroups.filter(o => parentUsergroupLimitation.includes(o.value.toString()));
                }
            }

            this.initElements = json.elements;

            EaseEventBus.listen('frameInfos', (frame) => {
                this.frame = _.extend({}, this.frame, frame);
                this.validateInit();
            });

            EaseEventBus.listen('doneUpdatingTemplate', (back) => {
                this.saving = false;

                if (back !== 'error') {
                    if (this.inline === 'true') {
                        if (back === true) {
                            window.location = this.back_url;
                        } else {
                            window.location = this.redirect_url;
                        }
                    } else {
                        window.location = this.redirect_url;
                    }
                }
            });

            EaseEventBus.listen('tap', (data) => {
                this.activeChanged = true;
                this.$nextTick(() => {
                    this.activeChanged = false;
                })
                this.active_dropdown = data.index;
            });

            EaseEventBus.listen('elementList', (data) => {
                let list = [];
                _.each(data, (element, index) => {
                    list.push({
                        'value': index,
                        'text': this.t_title(element, index),
                        'element': element
                    })
                });
                this.dropdown_elements = list;
                this.validateInit();
            });

            EaseEventBus.listen('removedElement', (id) => {
                if (this.creator_errors[id] != null) {
                    this.creator_errors.splice(id, 1);
                }
            });

            EaseEventBus.listen("unsavedChanges", (changes) => {
                this.unsavedChanges = changes;
            });

            EaseEventBus.listen('WORKAROUND_recalculate_settings_height', () => {
                this.timeoutHeightRecalculation();
            });

            EaseEventBus.listen('windowResize', (event) => {
                this.timeoutHeightRecalculation(true);
            });

            EaseEventBus.listen('collapseSettings', () => {
                this.settingsCollapsed = true;
            });
            EaseEventBus.listen('expandSettings', () => {
                this.settingsCollapsed = false;
            });

            this.timeoutHeightRecalculation();
        }
    }
</script>
